import { useMemo } from "react";
import { useBodyPalette } from "../../scene/BodyFinish";
import { GLASS, HEADLIGHT, TAIL_LIGHT } from "../../scene/materials";
import { panelBetween } from "../geometry/panels";
import { buildShell } from "../geometry/shell";
import { BUS, BUS_NOSE_X, BUS_TAIL_X } from "./dimensions";
import { busBodyProfile } from "./profiles";
const BEVEL = 0.1;
const SURFACE = BEVEL + 0.02;
const BODY_EXTRUDE = {
  depth: BUS.width - BEVEL * 2,
  bevelEnabled: true,
  bevelThickness: BEVEL,
  bevelSize: BEVEL,
  bevelSegments: 4,
  curveSegments: 24,
  steps: 1
};
const FLANK_Z = BUS.width / 2 + 4e-3;
function BusBody() {
  const palette = useBodyPalette("bus");
  const body = useMemo(
    () => buildShell(busBodyProfile(), BODY_EXTRUDE, { depth: BODY_EXTRUDE.depth }),
    []
  );
  const windscreen = useMemo(
    () => panelBetween(BUS.windscreenLower, BUS.windscreenUpper, BUS.width - 0.3, 0.04, SURFACE),
    []
  );
  const glazingLength = BUS.glazing.frontX - BUS.glazing.rearX;
  const glazingHeight = BUS.glazing.highY - BUS.glazing.lowY;
  return <group>
      <mesh geometry={body}>
        <meshPhysicalMaterial {...palette.body} />
      </mesh>

      <mesh position={windscreen.position} rotation={windscreen.rotation}>
        <boxGeometry args={windscreen.args} />
        <meshPhysicalMaterial {...GLASS} />
      </mesh>

      {
    /* continuous glazing band down both flanks */
  }
      {[-1, 1].map((side) => <mesh
    key={`glazing-${side}`}
    position={[
      (BUS.glazing.frontX + BUS.glazing.rearX) / 2,
      (BUS.glazing.lowY + BUS.glazing.highY) / 2,
      side * FLANK_Z
    ]}
  >
          <boxGeometry args={[glazingLength, glazingHeight, 0.02]} />
          <meshPhysicalMaterial {...GLASS} />
        </mesh>)}

      {
    /* Passenger doors, nearside only, framed so they read as openings in the band. */
  }
      {BUS.doors.map((door) => <group key={`door-${door.frontX}`}>
          <mesh
    position={[(door.frontX + door.rearX) / 2, 1.3, -FLANK_Z - 8e-3]}
  >
            <boxGeometry args={[door.frontX - door.rearX, 1.55, 0.02]} />
            <meshPhysicalMaterial {...GLASS} />
          </mesh>
          {[door.frontX, door.rearX].map((x) => <mesh key={`door-frame-${x}`} position={[x, 1.3, -FLANK_Z - 0.012]}>
              <boxGeometry args={[0.05, 1.6, 0.03]} />
              <meshPhysicalMaterial {...palette.edge} />
            </mesh>)}
        </group>)}

      {
    /* destination panel above the windscreen */
  }
      <mesh position={[BUS.windscreenUpper[0] - 0.1, BUS.roofY - 0.14, 0]}>
        <boxGeometry args={[0.36, 0.24, BUS.width - 0.5]} />
        <meshPhysicalMaterial {...palette.accent} />
      </mesh>

      {
    /* rear window, on the near-vertical rear panel */
  }
      <mesh position={[BUS_TAIL_X - SURFACE, 2.25, 0]}>
        <boxGeometry args={[0.04, 0.85, BUS.width - 0.55]} />
        <meshPhysicalMaterial {...GLASS} />
      </mesh>

      {
    /* front and rear bumpers */
  }
      <mesh position={[BUS_NOSE_X + SURFACE - 0.02, 0.62, 0]}>
        <boxGeometry args={[0.18, 0.4, BUS.width - 0.04]} />
        <meshPhysicalMaterial {...palette.edge} />
      </mesh>
      <mesh position={[BUS_TAIL_X - SURFACE + 0.02, 0.62, 0]}>
        <boxGeometry args={[0.18, 0.4, BUS.width - 0.04]} />
        <meshPhysicalMaterial {...palette.edge} />
      </mesh>

      {
    /* head and tail lamps */
  }
      {[-1, 1].map((side) => <mesh
    key={`headlight-${side}`}
    position={[BUS_NOSE_X + SURFACE, 1.16, side * (BUS.width / 2 - 0.42)]}
  >
          <boxGeometry args={[0.1, 0.3, 0.5]} />
          <meshPhysicalMaterial {...HEADLIGHT} />
        </mesh>)}
      {[-1, 1].map((side) => <mesh
    key={`tail-light-${side}`}
    position={[BUS_TAIL_X - SURFACE, 1.2, side * (BUS.width / 2 - 0.42)]}
  >
          <boxGeometry args={[0.1, 0.32, 0.44]} />
          <meshPhysicalMaterial {...TAIL_LIGHT} />
        </mesh>)}

      {
    /* roof air-conditioning pack */
  }
      <mesh position={[1.1, BUS.roofY + 0.11, 0]}>
        <boxGeometry args={[3.1, 0.22, BUS.width - 0.55]} />
        <meshPhysicalMaterial {...palette.chassis} />
      </mesh>

      {
    /* mirrors */
  }
      {[-1, 1].map((side) => <group key={`mirror-${side}`}>
          <mesh position={[BUS_NOSE_X - 0.42, 2.42, side * (BUS.width / 2 + 0.1)]}>
            <boxGeometry args={[0.05, 0.05, 0.22]} />
            <meshPhysicalMaterial {...palette.edge} />
          </mesh>
          <mesh position={[BUS_NOSE_X - 0.46, 2.24, side * (BUS.width / 2 + 0.23)]}>
            <boxGeometry args={[0.09, 0.42, 0.14]} />
            <meshPhysicalMaterial {...palette.edge} />
          </mesh>
        </group>)}

      {
    /* underbody, closing the tunnel the arch notches cut through the body */
  }
      <mesh position={[0, 0.5, 0]}>
        <boxGeometry args={[BUS.length - 1, 0.5, BUS.width - 0.8]} />
        <meshPhysicalMaterial {...palette.chassis} />
      </mesh>

      {Array.from({ length: 5 }, (_, i) => {
    const x = BUS.glazing.frontX - 0.4 - i * ((BUS.glazing.frontX - BUS.glazing.rearX - 0.8) / 4);
    return [-1, 1].map((side) => <mesh
      key={`pillar-${i}-${side}`}
      position={[x, (BUS.glazing.lowY + BUS.glazing.highY) / 2, side * (FLANK_Z + 0.01)]}
    >
            <boxGeometry args={[0.07, glazingHeight + 0.08, 0.04]} />
            <meshPhysicalMaterial {...palette.edge} />
          </mesh>);
  })}
      {[-1, 1].map((side) => <mesh
    key={`band-${side}`}
    position={[
      (BUS.glazing.frontX + BUS.glazing.rearX) / 2,
      BUS.glazing.lowY - 0.16,
      side * FLANK_Z
    ]}
  >
          <boxGeometry args={[glazingLength, 0.22, 0.03]} />
          <meshPhysicalMaterial {...palette.secondary} />
        </mesh>)}
      {[-1, 1].map((side) => <mesh key={`skirt-${side}`} position={[0, 0.72, side * (FLANK_Z - 0.02)]}>
          <boxGeometry args={[BUS.length - 1.4, 0.28, 0.04]} />
          <meshPhysicalMaterial {...palette.secondary} />
        </mesh>)}
    </group>;
}
export {
  BusBody
};
